import { useState, useRef, useEffect } from "react";
import { useSseAnalysis } from "./useSseAnalysis";
import { UploadedDocument, validateAndReadFiles } from "./types";
import { useGetWorkbenchItems, useGetCompanyProfiles } from "../useAiWorkbench";
import { useAuth } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileText, X, Bot, Loader2, AlertTriangle, ShieldAlert } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useToast } from "@/hooks/use-toast";

export function BidCheckTab() {
  const { getToken } = useAuth();
  const { data: workbenchData } = useGetWorkbenchItems();
  const { data: profilesData } = useGetCompanyProfiles();
  const { toast } = useToast();
  
  const workbenchItems = workbenchData?.items ?? [];
  const profiles = profilesData?.profiles ?? [];

  const [selectedTenderId, setSelectedTenderId] = useState<string>("");
  const [selectedProfileId, setSelectedProfileId] = useState<string>("none");
  const [extraText, setExtraText] = useState("");
  
  const [pdfFiles, setPdfFiles] = useState<File[]>([]);
  const [pdfBase64s, setPdfBase64s] = useState<UploadedDocument[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [precheckLoading, setPrecheckLoading] = useState(false);
  const [precheckWarning, setPrecheckWarning] = useState<string | null>(null);
  const isTenderDocRef = useRef<boolean | null>(null);

  const { result, error, loading, runAnalysis, abort, reset: resetAnalysis } = useSseAnalysis();
  const resultRef = useRef<HTMLDivElement>(null);

  // Set default profile if available
  useEffect(() => {
    if (profiles.length > 0 && selectedProfileId === "none") {
      const def = profiles.find(p => p.isDefault);
      if (def) setSelectedProfileId(def.id.toString());
    }
  }, [profiles, selectedProfileId]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.length) addFiles(Array.from(e.target.files));
    e.target.value = "";
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) addFiles(Array.from(e.dataTransfer.files));
  }

  async function addFiles(newFiles: File[]) {
    const r = await validateAndReadFiles(newFiles, pdfFiles);
    
    if (r.error) {
      toast({ title: "檔案錯誤", description: r.error, variant: "destructive" });
    }
    
    if (r.validFiles.length > 0) {
      setPdfFiles(prev => [...prev, ...r.validFiles]);
      setPdfBase64s(prev => [...prev, ...r.base64s]);
    }
  }

  function removeFile(idx: number) {
    setPdfFiles(prev => prev.filter((_, i) => i !== idx));
    setPdfBase64s(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleAnalyzeClick() {
    if (!selectedTenderId) {
      toast({ title: "未選擇", description: "請選擇工作台招標。" });
      return;
    }
    if (pdfBase64s.length === 0) {
      toast({ title: "未上載文件", description: "請上載已填寫的標書文件。" });
      return;
    }

    setPrecheckLoading(true);
    setPrecheckWarning(null);
    isTenderDocRef.current = null;
    
    try {
      const token = await getToken();
      const r = await fetch("/api/admin/ai-assistant/precheck", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ documents: pdfBase64s }),
      });
      const data = await r.json();
      if (!data.isTender) {
        isTenderDocRef.current = false;
        setPrecheckWarning(data.reason || "此文件可能並非招標文件。");
        setPrecheckLoading(false);
        return;
      }
      isTenderDocRef.current = true;
    } catch {
      // Fail open
    } finally {
      setPrecheckLoading(false);
    }

    startAnalysis();
  }

  function startAnalysis() {
    setPrecheckWarning(null);
    runAnalysis({
      mode: 2,
      documents: pdfBase64s,
      extraText: extraText || undefined,
      isTenderDoc: isTenderDocRef.current,
      tenderId: parseInt(selectedTenderId),
      companyProfileId: selectedProfileId !== "none" ? parseInt(selectedProfileId) : undefined
    });
  }

  function reset() {
    resetAnalysis();
    setPdfFiles([]);
    setPdfBase64s([]);
    setExtraText("");
    setPrecheckWarning(null);
    isTenderDocRef.current = null;
  }

  // Auto-scroll
  useEffect(() => {
    if (result && resultRef.current) {
      resultRef.current.scrollTop = resultRef.current.scrollHeight;
    }
  }, [result]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-6">
        <div className="bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 rounded-lg p-4 space-y-2">
          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-300 font-semibold">
            <ShieldAlert className="w-5 h-5" />
            私隱與安全聲明
          </div>
          <p className="text-sm text-blue-700/90 dark:text-blue-400/90 leading-relaxed">
            Skoltend 不會儲存上載的檔案或其敏感內容。文件僅作暫存，並直接傳送至所選的 AI 供應商進行分析。
          </p>
          <p className="text-xs text-blue-600/70 dark:text-blue-500/70 leading-relaxed">
            免責聲明：AI 僅作輔助用途，不能保證內容完全正確或確保中標。請務必人工核對所有提交內容。
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">目標招標（來自工作台）</label>
            <Select value={selectedTenderId} onValueChange={setSelectedTenderId}>
              <SelectTrigger>
                <SelectValue placeholder="選擇工作台招標..." />
              </SelectTrigger>
              <SelectContent>
                {workbenchItems.length === 0 ? (
                  <SelectItem value="empty" disabled>工作台沒有項目</SelectItem>
                ) : (
                  workbenchItems.map(item => (
                    <SelectItem key={item.id} value={item.tender.id.toString()}>
                      {item.tender.title}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">公司資料（選填）</label>
            <Select value={selectedProfileId} onValueChange={setSelectedProfileId}>
              <SelectTrigger>
                <SelectValue placeholder="選擇公司資料以協助核對..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">無（僅作一般審核）</SelectItem>
                {profiles.map(p => (
                  <SelectItem key={p.id} value={p.id.toString()}>
                    {p.name} {p.isDefault ? "（預設）" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">已填寫的標書文件</label>
            {pdfFiles.length > 0 && (
              <div className="space-y-1.5 mb-2">
                {pdfFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border bg-muted/30">
                    <FileText className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-sm flex-1 truncate">{file.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">
                      {(file.size / 1024).toFixed(0)} KB
                    </span>
                    <button onClick={() => removeFile(idx)} className="shrink-0 text-muted-foreground hover:text-destructive transition-colors">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <label
              className={`flex items-center justify-center gap-3 px-4 py-4 rounded-lg border-2 border-dashed cursor-pointer transition-all ${
                isDragging ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/20"
              }`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <Upload className={`h-5 w-5 shrink-0 ${isDragging ? "text-primary" : "text-muted-foreground"}`} />
              <div>
                <p className="text-sm font-medium">拖入檔案或點擊上載</p>
                <p className="text-xs text-muted-foreground">支援 PDF、DOC、DOCX、TXT。最多 10 個檔案，總計 50MB。</p>
              </div>
              <input type="file" ref={fileInputRef} multiple onChange={handleFileChange} className="hidden" accept=".pdf,.doc,.docx,.txt" />
            </label>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">補充說明 / 疑問</label>
            <Textarea 
              value={extraText} 
              onChange={e => setExtraText(e.target.value)} 
              placeholder="例如：請檢查報價結構是否符合招標要求..."
              rows={3}
            />
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button onClick={handleAnalyzeClick} disabled={loading || precheckLoading || !selectedTenderId || pdfFiles.length === 0} className="gap-2 w-full sm:w-auto">
              {precheckLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> 檢查文件中...</>
              ) : loading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> 分析中...</>
              ) : (
                <><Bot className="h-4 w-4" /> 開始標書審核</>
              )}
            </Button>
            {(result || pdfFiles.length > 0 || extraText) && !loading && (
              <Button variant="ghost" onClick={reset}>重置</Button>
            )}
            {loading && (
              <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={abort}>停止</Button>
            )}
          </div>
          
          {precheckWarning && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 p-4 mt-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-amber-800">AI 偵測到此文件可能並非招標文件</p>
                  <p className="text-sm text-amber-700 mt-1">{precheckWarning}</p>
                  <div className="flex items-center gap-2 mt-3">
                    <Button size="sm" onClick={startAnalysis} className="bg-amber-600 hover:bg-amber-700 text-white">
                      強制繼續分析
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setPrecheckWarning(null)}>
                      取消
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive mt-4">
              {error}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col min-h-[500px] md:h-[calc(100vh-16rem)] border rounded-lg bg-card">
        <div className="p-4 border-b flex items-center gap-2 bg-muted/20">
          <Bot className="h-4 w-4 text-primary" />
          <h2 className="font-semibold text-sm">審核結果</h2>
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground ml-auto" />}
        </div>
        <div ref={resultRef} className="flex-1 overflow-y-auto p-6">
          {!result && !loading ? (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground opacity-50">
              <ShieldAlert className="h-12 w-12 mb-3" />
              <p>上載已填寫的標書並執行審核。</p>
            </div>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:font-semibold prose-a:text-primary">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{result}</ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
